import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { CheckCircle2, XCircle, Smartphone, RefreshCw, Copy, Send } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import api from '../lib/api-client';
import { logger } from '../utils/logger';
import { toast } from 'sonner';

interface TokenStatus {
  hasToken: boolean;
  token?: string;
  platform?: string;
  storedToken?: string;
  storedPlatform?: string;
  isNativeApp: boolean;
  lastAttempt?: string;
  registrationStatus?: 'pending' | 'success' | 'error';
}

interface APILog {
  timestamp: string;
  method: string;
  url: string;
  status?: number;
  data?: any;
  error?: any;
}

export function ExpoTokenDebugPanelEnhanced() {
  const [isVisible, setIsVisible] = useState(false);
  const [tokenStatus, setTokenStatus] = useState<TokenStatus | null>(null);
  const [apiLogs, setApiLogs] = useState<APILog[]>([]);
  const [isTesting, setIsTesting] = useState(false);
  const { user } = useAuth();

  // Show panel via console command
  useEffect(() => {
    (window as any).__showExpoDebug = () => {
      setIsVisible(true);
      checkTokenStatus();
    };
    (window as any).__hideExpoDebug = () => setIsVisible(false);
    (window as any).__getExpoStatus = () => {
      checkTokenStatus();
      return tokenStatus;
    };
    
    logger.log('Expo Token Debug Panel available. Commands:');
    logger.log('  __showExpoDebug() - Open debug panel');
    logger.log('  __getExpoStatus() - Get current token status');
    
    return () => {
      delete (window as any).__showExpoDebug;
      delete (window as any).__hideExpoDebug;
      delete (window as any).__getExpoStatus;
    };
  }, []);

  // Intercept fetch to log API calls
  useEffect(() => {
    const originalFetch = window.fetch;
    
    window.fetch = async (...args) => {
      const [url, options] = args;
      const urlStr = typeof url === 'string' ? url : url.toString();
      
      // Only log expo-related calls
      if (urlStr.includes('expo') || urlStr.includes('push-token')) {
        const logEntry: APILog = {
          timestamp: new Date().toLocaleTimeString(),
          method: options?.method || 'GET',
          url: urlStr,
        };
        
        logger.log('🌐 API Call Intercepted:', logEntry);
        
        try {
          const response = await originalFetch(...args);
          const clonedResponse = response.clone();
          
          logEntry.status = response.status;
          
          try {
            logEntry.data = await clonedResponse.json();
          } catch {
            // Response not JSON
          }
          
          setApiLogs(prev => [logEntry, ...prev].slice(0, 10));
          logger.log('✅ API Response:', logEntry);
          
          return response;
        } catch (error) {
          logEntry.error = error;
          setApiLogs(prev => [logEntry, ...prev].slice(0, 10));
          logger.error('❌ API Error:', logEntry);
          throw error;
        }
      }
      
      return originalFetch(...args);
    };
    
    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  const checkTokenStatus = () => {
    const isNativeApp = !!(window as any).isNativeApp;
    const token = (window as any).expoPushToken;
    const platform = (window as any).devicePlatform;
    const storedToken = localStorage.getItem('pending_expo_push_token');
    const storedPlatform = localStorage.getItem('pending_expo_platform');
    
    const status: TokenStatus = {
      hasToken: !!(token || storedToken),
      token,
      platform,
      storedToken: storedToken || undefined,
      storedPlatform: storedPlatform || undefined,
      isNativeApp,
      lastAttempt: new Date().toLocaleTimeString()
    };
    
    setTokenStatus(status);
    
    logger.log('📊 Token Status Check:', status);
    logger.log('User Status:', {
      isLoggedIn: !!user,
      userId: user?._id,
      userName: user ? `${user.firstName} ${user.lastName}` : 'Not logged in'
    });
    
    return status;
  };

  const testTokenRegistration = async () => {
    setIsTesting(true);
    
    try {
      const status = checkTokenStatus();
      const tokenToTest = status.token || status.storedToken;
      
      if (!tokenToTest) {
        toast.error('No token available to test');
        logger.error('❌ No token available for testing');
        setIsTesting(false);
        return;
      }
      
      if (!user) {
        toast.error('Please login first to test token registration');
        logger.error('❌ User not logged in');
        setIsTesting(false);
        return;
      }
      
      logger.log('🧪 Testing token registration...');
      logger.log('  Token:', tokenToTest.substring(0, 30) + '...');
      logger.log('  Platform:', status.platform || status.storedPlatform);
      logger.log('  User:', user._id);
      logger.log('  API URL:', api.defaults.baseURL + '/expo/update-push-token');
      
      const response = await api.post('/expo/update-push-token', {
        expoPushToken: tokenToTest,
        platform: status.platform || status.storedPlatform || 'unknown',
        device: navigator.userAgent
      });
      
      logger.log('✅ Token registration successful!', response.data);
      toast.success('Token registered successfully!');
      
      setTokenStatus(prev => prev ? { ...prev, registrationStatus: 'success' } : null);
    } catch (error: any) {
      logger.error('❌ Token registration failed:', error);
      logger.error('  Status:', error.response?.status);
      logger.error('  Message:', error.message);
      logger.error('  Response:', error.response?.data);
      
      toast.error('Token registration failed: ' + (error.response?.data?.message || error.message));
      setTokenStatus(prev => prev ? { ...prev, registrationStatus: 'error' } : null);
    } finally {
      setIsTesting(false);
    }
  };

  const requestToken = () => {
    if ((window as any).requestPushToken) {
      logger.log('📱 Requesting push token from native app...');
      (window as any).requestPushToken();
      toast.info('Token request sent to native app');
      
      // Check status after delay
      setTimeout(checkTokenStatus, 2000);
    } else {
      toast.error('Not in native app environment');
      logger.error('❌ requestPushToken function not available');
    }
  };

  const copyToken = (token?: string) => {
    if (token) {
      navigator.clipboard.writeText(token);
      toast.success('Token copied to clipboard');
    }
  };

  const simulateTokenReceived = () => {
    const testToken = 'ExponentPushToken[xxxxxxxxxxxxxxxxxxxxxx]';
    const testPlatform = 'test';
    
    logger.log('🧪 Simulating token received event...');
    
    const event = new CustomEvent('expoPushTokenReceived', {
      detail: { token: testToken, platform: testPlatform }
    });
    
    window.dispatchEvent(event);
    toast.info('Simulated token received event');
    
    setTimeout(checkTokenStatus, 500);
  };

  useEffect(() => {
    if (isVisible) {
      checkTokenStatus();
      
      // Check every 3 seconds while panel is open
      const interval = setInterval(checkTokenStatus, 3000);
      return () => clearInterval(interval);
    }
  }, [isVisible, user]);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4">
      <Card className="max-w-4xl w-full max-h-[90vh] overflow-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="size-5" />
            Expo Token Debug Panel
          </CardTitle>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={checkTokenStatus}
            >
              <RefreshCw className="size-4 mr-1" />
              Refresh
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setIsVisible(false)}
            >
              Close
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Environment Status */}
          <div className="space-y-2">
            <h3 className="font-semibold">Environment</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                {tokenStatus?.isNativeApp ? (
                  <CheckCircle2 className="size-4 text-green-500" />
                ) : (
                  <XCircle className="size-4 text-orange-500" />
                )}
                <span>Native App: {tokenStatus?.isNativeApp ? 'Yes' : 'No (Web)'}</span>
              </div>
              <div className="flex items-center gap-2">
                {user ? (
                  <CheckCircle2 className="size-4 text-green-500" />
                ) : (
                  <XCircle className="size-4 text-red-500" />
                )}
                <span>User Logged In: {user ? 'Yes' : 'No'}</span>
              </div>
              {user && (
                <div className="col-span-2">
                  <span className="text-muted-foreground">User: </span>
                  <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                    {user.firstName} {user.lastName} ({user._id})
                  </code>
                </div>
              )}
            </div>
          </div>

          {/* Token Status */}
          <div className="space-y-2">
            <h3 className="font-semibold">Token Status</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {tokenStatus?.hasToken ? (
                    <CheckCircle2 className="size-4 text-green-500" />
                  ) : (
                    <XCircle className="size-4 text-red-500" />
                  )}
                  <span>Token Available: {tokenStatus?.hasToken ? 'Yes' : 'No'}</span>
                </div>
                {tokenStatus?.registrationStatus && (
                  <Badge variant={tokenStatus.registrationStatus === 'success' ? 'default' : 'destructive'}>
                    {tokenStatus.registrationStatus}
                  </Badge>
                )}
              </div>

              {tokenStatus?.token && (
                <div className="bg-green-50 border border-green-200 rounded p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">Current Token:</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToken(tokenStatus.token)}
                    >
                      <Copy className="size-3 mr-1" />
                      Copy
                    </Button>
                  </div>
                  <code className="text-xs break-all bg-white px-2 py-1 rounded block">
                    {tokenStatus.token}
                  </code>
                  <div className="text-xs text-muted-foreground mt-1">
                    Platform: {tokenStatus.platform || 'unknown'}
                  </div>
                </div>
              )}

              {tokenStatus?.storedToken && (
                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">Stored Token (Pending):</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToken(tokenStatus.storedToken)}
                    >
                      <Copy className="size-3 mr-1" />
                      Copy
                    </Button>
                  </div>
                  <code className="text-xs break-all bg-white px-2 py-1 rounded block">
                    {tokenStatus.storedToken}
                  </code>
                  <div className="text-xs text-muted-foreground mt-1">
                    Platform: {tokenStatus.storedPlatform || 'unknown'}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2">
            <Button 
              onClick={testTokenRegistration} 
              disabled={isTesting || !tokenStatus?.hasToken || !user}
              className="flex items-center gap-2"
            >
              <Send className="size-4" />
              {isTesting ? 'Testing...' : 'Test Token Registration'}
            </Button>
            
            {tokenStatus?.isNativeApp && (
              <Button 
                onClick={requestToken}
                variant="outline"
              >
                <Smartphone className="size-4 mr-1" />
                Request Token
              </Button>
            )}
            
            <Button 
              onClick={simulateTokenReceived}
              variant="outline"
            >
              Simulate Token Event
            </Button>
          </div>

          {/* API Call Logs */}
          {apiLogs.length > 0 && (
            <div className="space-y-2">
              <h3 className="font-semibold">Recent API Calls</h3>
              <div className="space-y-2 max-h-60 overflow-auto">
                {apiLogs.map((log, index) => (
                  <div 
                    key={index}
                    className="border rounded p-2 text-xs space-y-1"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant={log.status && log.status < 400 ? 'default' : 'destructive'}>
                          {log.method}
                        </Badge>
                        <span className="text-muted-foreground">{log.timestamp}</span>
                      </div>
                      {log.status && (
                        <Badge variant={log.status < 400 ? 'default' : 'destructive'}>
                          {log.status}
                        </Badge>
                      )}
                    </div>
                    <code className="text-xs break-all">{log.url}</code>
                    {log.data && (
                      <pre className="bg-gray-50 p-2 rounded text-xs overflow-auto max-h-20">
                        {JSON.stringify(log.data, null, 2)}
                      </pre>
                    )}
                    {log.error && (
                      <div className="text-red-600">
                        Error: {log.error.message || String(log.error)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2 text-sm">
            <h4 className="font-semibold">Debugging Steps:</h4>
            <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
              <li>Check if "Native App" shows Yes (you're in Expo app)</li>
              <li>Check if "User Logged In" shows Yes</li>
              <li>Click "Request Token" to get push token from Expo</li>
              <li>Check if token appears in "Current Token" section</li>
              <li>Click "Test Token Registration" to verify API is working</li>
              <li>Check "Recent API Calls" for request/response details</li>
              <li>Look for status 200 in API calls (means success)</li>
            </ol>
          </div>

          {/* Console Commands */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2 text-sm">
            <h4 className="font-semibold">Console Commands:</h4>
            <div className="space-y-1 font-mono text-xs">
              <div><code className="bg-white px-2 py-1 rounded">__showExpoDebug()</code> - Show this panel</div>
              <div><code className="bg-white px-2 py-1 rounded">__getExpoStatus()</code> - Get token status</div>
              <div><code className="bg-white px-2 py-1 rounded">window.expoPushToken</code> - Check current token</div>
              <div><code className="bg-white px-2 py-1 rounded">window.requestPushToken()</code> - Request new token</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}